import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson } from "@/data/api";

// Workspace API keys for the transcript egress pull API (/api/v1). The full
// token is returned exactly once at mint time; only the prefix is listable.

export interface WorkspaceApiKeyRow {
  id: string;
  name: string;
  token_prefix: string;
  created_at: string | number;
  last_used_at: string | number | null;
}

interface ApiKeysResponse {
  api_keys: WorkspaceApiKeyRow[];
}

export interface MintedApiKey {
  api_key: WorkspaceApiKeyRow;
  token: string;
}

const apiKeyKeys = {
  list: (workspaceSlug: string | null | undefined) =>
    ["api-keys", workspaceSlug ?? null] as const,
};

export function useWorkspaceApiKeysQuery(
  workspaceSlug: string | null | undefined,
) {
  return useQuery({
    queryKey: apiKeyKeys.list(workspaceSlug),
    queryFn: () => listApiKeys(workspaceSlug ?? ""),
    enabled: Boolean(workspaceSlug),
    staleTime: 15_000,
  });
}

export function useMintApiKeyMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => mintApiKey(workspaceSlug ?? "", name),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: apiKeyKeys.list(workspaceSlug),
      });
    },
  });
}

export function useDeleteApiKeyMutation(workspaceSlug: string | null) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (keyId: string) => deleteApiKey(workspaceSlug ?? "", keyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: apiKeyKeys.list(workspaceSlug),
      });
    },
  });
}

function listApiKeys(workspaceSlug: string) {
  return apiJson<ApiKeysResponse>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/api-keys`,
  );
}

function mintApiKey(workspaceSlug: string, name: string) {
  return apiJson<MintedApiKey>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/api-keys`,
    {
      method: "POST",
      json: { name },
    },
  );
}

function deleteApiKey(workspaceSlug: string, keyId: string) {
  return apiJson<{ ok: true }>(
    `/workspaces/${encodeURIComponent(workspaceSlug)}/api-keys/${encodeURIComponent(
      keyId,
    )}`,
    { method: "DELETE" },
  );
}
